// Angular
import { inject, Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Router } from "@angular/router";

// RX JS
import { BehaviorSubject, catchError, finalize, Observable, of } from "rxjs";

// Services
import { CookieService } from "ngx-cookie-service";
import { LoaderService } from "#services/loader/loader.service";
import { HttpService } from "#services/http/http.service";
import { UserService } from "#services/user/user.service";

// Models
import { CustomHttpErrorResponse } from "#models/custom-http-error-response";
import { User } from "#models/user";

// Other
import { environment } from "#src/environments/environment";
import { AUTH_PATH } from "#src/app/shared/variables/routes";

@Injectable({
    providedIn: "root",
})
export class AuthService extends HttpService {
    apiUrl = `${environment.API_URL}/auth`;
    tokenUrl = `${environment.API_URL}/csrf-cookie`;
    loginUrl = `${this.apiUrl}/login`;
    logoutUrl = `${this.apiUrl}/logout`;
    checkAuthUrl = `${this.apiUrl}/user`;

    private router = inject(Router);
    private userService = inject(UserService);
    private loaderService = inject(LoaderService);
    private cookieService = inject(CookieService);

    public isAuthenticatedSubject = new BehaviorSubject<boolean>(true);

    get isAuthenticated$(): Observable<boolean> {
        return this.isAuthenticatedSubject.asObservable();
    }

    getToken() {
        return this.http.get(this.tokenUrl).pipe(
            catchError((error) => {
                this.handleRequestError(error);

                return of({});
            })
        );
    }

    login(formGroup: FormGroup, onError?: (err: CustomHttpErrorResponse) => void) {
        this.loaderService.show();

        return new Promise((resolve) => {
            this.getToken().subscribe(() => {
                this.http
                    .post<{ message: string; data: User }>(this.loginUrl, formGroup.value)
                    .pipe(
                        catchError((error) => {
                            onError?.(error);
                            return this.handleRequestValidationError(error, formGroup);
                        }),
                        finalize(() => {
                            this.loaderService.hide();
                            resolve(true);
                        })
                    )
                    .subscribe((response: { message: string; data: User }) => {
                        if (!response.data) {
                            this.isAuthenticatedSubject.next(false);
                            return;
                        }

                        this.userService.setUser(response.data);
                        this.isAuthenticatedSubject.next(true);

                        this.router.navigate(["/"]);
                    });
            });
        });
    }

    logout() {
        this.http
            .post(this.logoutUrl, {})
            .pipe(
                catchError((error) => {
                    this.handleRequestError(error);
                    return of({});
                }),
                finalize(() => {
                    this.softLogout();
                })
            )
            .subscribe();
    }

    softLogout() {
        this.userService.clearUser();
        this.isAuthenticatedSubject.next(false);
        this.cookieService.delete("XSRF-TOKEN");
        this.router.navigate([`/${AUTH_PATH}`]);
    }

    checkAuth() {
        return this.http.get<{ message: string; data: User | null }>(this.checkAuthUrl).pipe(
            catchError(() => {
                return of({ data: null, message: "" });
            })
        );
    }

    async isLoggedIn(): Promise<boolean> {
        return new Promise((resolve) => {
            if (!this.isAuthenticatedSubject.getValue()) {
                return resolve(false);
            }

            if (this.userService.getUser()) {
                return resolve(true);
            }

            this.isAuthenticatedSubject.next(false);
            this.checkAuth().subscribe((response: { message: string; data: User | null }) => {
                if (response.data) {
                    this.isAuthenticatedSubject.next(true);
                    this.userService.setUser(response.data);

                    resolve(this.isAuthenticatedSubject.getValue());
                } else {
                    resolve(false);
                }
            });
        });
    }
}
